/**
 * WhatsApp Business Settings — admin page for configuring
 * WhatsApp Cloud API integration (phone number, credentials, defaults).
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useEffect, useState } from 'react'
import { Phone, PhoneCall, Save, CheckCircle, AlertCircle, User, Info } from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Textarea,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Spinner,
} from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import { usePersonas } from '@/hooks'
import type { WhatsAppConfig } from '@/types/inbox'

// snake_case → camelCase
function transformConfig(raw: Record<string, unknown>): WhatsAppConfig | null {
  if (!raw) return null
  return {
    id: raw.id as string,
    userId: raw.user_id as string,
    phoneNumberId: raw.phone_number_id as string,
    businessAccountId: (raw.business_account_id as string) ?? null,
    webhookVerifyToken: raw.webhook_verify_token as string,
    defaultPersonaId: (raw.default_persona_id as string) ?? null,
    isActive: raw.is_active as boolean,
    voiceResponseEnabled: raw.voice_response_enabled as boolean,
    autoReplyEnabled: raw.auto_reply_enabled as boolean,
    callingEnabled: raw.calling_enabled as boolean,
    greetingMessage: (raw.greeting_message as string) ?? null,
    businessHours: (raw.business_hours as Record<string, unknown>) ?? null,
    config: (raw.config as Record<string, unknown>) ?? null,
    hasAccessToken: raw.has_access_token as boolean,
    hasAppSecret: raw.has_app_secret as boolean,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
  }
}

export function WhatsAppSettingsPage() {
  const [config, setConfig] = useState<WhatsAppConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [phoneNumberId, setPhoneNumberId] = useState('')
  const [businessAccountId, setBusinessAccountId] = useState('')
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [appSecret, setAppSecret] = useState('')
  const [defaultPersonaId, setDefaultPersonaId] = useState<string | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(true)
  const [voiceResponseEnabled, setVoiceResponseEnabled] = useState(false)
  const [callingEnabled, setCallingEnabled] = useState(false)
  const [greetingMessage, setGreetingMessage] = useState('')

  const { personas } = usePersonas()

  useEffect(() => {
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.fetch('/api/whatsapp/config')
      if (!response.ok) {
        if (response.status === 404) {
          // No config yet
          setConfig(null)
          setIsLoading(false)
          return
        }
        throw new Error(`Failed to fetch: ${response.statusText}`)
      }
      const data = await response.json()
      if (data) {
        const c = transformConfig(data)
        setConfig(c)
        if (c) {
          setPhoneNumberId(c.phoneNumberId || '')
          setBusinessAccountId(c.businessAccountId || '')
          setWebhookVerifyToken(c.webhookVerifyToken || '')
          setDefaultPersonaId(c.defaultPersonaId || null)
          setIsActive(c.isActive)
          setAutoReplyEnabled(c.autoReplyEnabled)
          setVoiceResponseEnabled(c.voiceResponseEnabled)
          setCallingEnabled(c.callingEnabled)
          setGreetingMessage(c.greetingMessage || '')
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load config')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!phoneNumberId.trim() || !webhookVerifyToken.trim()) {
      setError('Phone Number ID and Webhook Verify Token are required')
      return
    }

    // Require credentials for new config or when updating them
    if (!config && (!accessToken.trim() || !appSecret.trim())) {
      setError('Access Token and App Secret are required for initial setup')
      return
    }

    setIsSaving(true)
    setError(null)
    setSaveSuccess(false)

    try {
      const body: Record<string, unknown> = {
        phone_number_id: phoneNumberId.trim(),
        business_account_id: businessAccountId.trim() || null,
        webhook_verify_token: webhookVerifyToken.trim(),
        default_persona_id: defaultPersonaId || null,
        is_active: isActive,
        auto_reply_enabled: autoReplyEnabled,
        voice_response_enabled: voiceResponseEnabled,
        calling_enabled: callingEnabled,
        greeting_message: greetingMessage.trim() || null,
      }

      if (accessToken.trim()) body.access_token = accessToken.trim()
      if (appSecret.trim()) body.app_secret = appSecret.trim()

      // For new configs, always use PUT which does upsert with all required fields
      if (!config) {
        body.access_token = accessToken.trim()
        body.app_secret = appSecret.trim()
      }

      const response = await apiClient.fetch('/api/whatsapp/config', {
        method: config ? 'PATCH' : 'PUT',
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const err = await response.text()
        throw new Error(`Failed to save: ${err}`)
      }

      const data = await response.json()
      setConfig(transformConfig(data))
      setAccessToken('')
      setAppSecret('')
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`

  return (
    <PageFrame width="form" className="space-y-6">
      <PageHeader
        title="WhatsApp Business Settings"
        icon={Phone}
        tag={
          config && (
            <Badge variant={config.isActive ? 'default' : 'secondary'}>
              {config.isActive ? 'Active' : 'Inactive'}
            </Badge>
          )
        }
      />

      {/* Webhook URL info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Webhook Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Webhook URL (set this in Meta Developer Console)
            </label>
            <div className="flex items-center gap-2 mt-1">
              <Input value={webhookUrl} readOnly className="text-sm font-mono bg-muted" />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigator.clipboard.writeText(webhookUrl)}
              >
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meta API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Meta API Credentials</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Phone Number ID *
            </label>
            <Input
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="e.g., 123456789012345"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Business Account ID
            </label>
            <Input
              value={businessAccountId}
              onChange={(e) => setBusinessAccountId(e.target.value)}
              placeholder="e.g., 987654321098765"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Webhook Verify Token *
            </label>
            <Input
              value={webhookVerifyToken}
              onChange={(e) => setWebhookVerifyToken(e.target.value)}
              placeholder="A secret token for webhook verification"
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Access Token {config?.hasAccessToken && <span className="text-success">(configured)</span>}
            </label>
            <Input
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder={config?.hasAccessToken ? 'Leave empty to keep current' : 'Meta API access token'}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              App Secret {config?.hasAppSecret && <span className="text-success">(configured)</span>}
            </label>
            <Input
              type="password"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder={config?.hasAppSecret ? 'Leave empty to keep current' : 'Meta app secret'}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Behavior Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Behavior</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Default Persona */}
          <div>
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" />
              Default Persona *
            </label>
            <Select
              value={defaultPersonaId || '__none__'}
              onValueChange={(value) => setDefaultPersonaId(value === '__none__' ? null : value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a persona..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-muted-foreground">No persona selected</span>
                </SelectItem>
                {personas.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <span>{p.name}</span>
                      {p.whatsappConfig?.enabled && (
                        <span className="text-xs text-green-500">WA</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              The persona that handles incoming WhatsApp messages. Must have WhatsApp enabled in its Channels settings.
            </p>
            {defaultPersonaId && personas.length > 0 && (() => {
              const selected = personas.find(p => p.id === defaultPersonaId)
              if (selected && !selected.whatsappConfig?.enabled) {
                return (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-warning-strong">
                    <AlertCircle className="w-3.5 h-3.5" />
                    This persona does not have WhatsApp enabled. Edit the persona and enable it in the Channels tab.
                  </div>
                )
              }
              return null
            })()}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Active</p>
              <p className="text-xs text-muted-foreground">Enable/disable WhatsApp integration</p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} aria-label="Active" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">AI Auto-Reply</p>
              <p className="text-xs text-muted-foreground">AI automatically responds to messages</p>
            </div>
            <Switch checked={autoReplyEnabled} onCheckedChange={setAutoReplyEnabled} aria-label="AI Auto-Reply" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Voice Responses</p>
              <p className="text-xs text-muted-foreground">Reply with audio messages (TTS)</p>
            </div>
            <Switch checked={voiceResponseEnabled} onCheckedChange={setVoiceResponseEnabled} aria-label="Voice Responses" />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Greeting Message
            </label>
            <Textarea
              value={greetingMessage}
              onChange={(e) => setGreetingMessage(e.target.value)}
              placeholder="Hello! Welcome to our service. How can I help you today?"
              rows={3}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Sent to new customers on their first message
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Voice Calling */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-green-500" />
            Voice Calling
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Enable Voice Calling</p>
              <p className="text-xs text-muted-foreground">Accept inbound WhatsApp voice calls with AI personas</p>
            </div>
            <Switch checked={callingEnabled} onCheckedChange={setCallingEnabled} aria-label="Enable Voice Calling" />
          </div>

          {callingEnabled && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-info/10 rounded-lg">
                <Info className="w-4 h-4 text-info mt-0.5 shrink-0" />
                <div className="text-xs text-info space-y-1">
                  <p className="font-medium">Prerequisites for voice calling:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>Subscribe to the <span className="font-mono">calls</span> webhook field in Meta Business Suite</li>
                    <li>Ensure your account has a messaging limit of 2,000+ conversations/24h</li>
                    <li>Configure Deepgram and ElevenLabs API keys in Settings</li>
                    <li>Enable calling on individual personas in their Channels tab</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error / Success */}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      {saveSuccess && (
        <div className="flex items-center gap-2 text-success text-sm">
          <CheckCircle className="w-4 h-4" />
          Settings saved successfully
        </div>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <Spinner size="sm" className="mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>
    </PageFrame>
  )
}
